import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Eye, EyeOff, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password }, { withCredentials: true });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center p-4 sm:p-8 relative">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-aura-purple/20 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 sm:mb-12">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-serif font-medium text-text-light">Memouraa</span>
        </Link>

        {done ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-7 h-7 text-success" />
            </div>
            <h1 className="text-3xl font-serif font-medium text-text-light mb-3">Password updated</h1>
            <p className="text-text-muted mb-8">You can now sign in with your new password. Redirecting&hellip;</p>
            <Link to="/login" className="text-champagne font-medium hover:underline">Go to sign in</Link>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="text-3xl font-serif font-medium text-text-light mb-3">Invalid reset link</h1>
            <p className="text-text-muted mb-8">This link is missing its token. Please request a new one.</p>
            <Link to="/forgot-password" className="text-champagne font-medium hover:underline">Request a new link</Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light mb-2">Create a new password</h1>
            <p className="text-text-muted mb-8">Choose a strong password you don&rsquo;t use elsewhere.</p>

            {error && (
              <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-text-light mb-2">New password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="aura-input pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-champagne transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-light mb-2">Confirm new password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="aura-input"
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-champagne flex items-center justify-center gap-2 py-4"
              >
                {loading ? <span className="animate-pulse">Updating...</span> : (<>Update password<ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
